import {
  cancelSpatialVisionBatch,
  cancelSpatialVisionJob,
  cancelSpatialVisionJobItem,
  cancelSpatialVisionSession,
  createSpatialVisionCondition,
  createSpatialVisionDirectJob,
  createSpatialVisionReferenceImage,
  createSpatialVisionSequence,
  createSpatialVisionSequenceJob,
  createSpatialVisionStep,
  deleteSpatialVisionCondition,
  deleteSpatialVisionReferenceImage,
  deleteSpatialVisionReferenceImageById,
  deleteSpatialVisionSequence,
  deleteSpatialVisionStep,
  getSpatialVisionJob,
  getSpatialVisionSequence,
  getSpatialVisionSettings,
  getSpatialVisionVlmVersions,
  listSpatialVisionJobs,
  listSpatialVisionSequences,
  listSpatialVisionSessions,
  runSpatialVisionChat,
  runSpatialVisionChatBatch,
  runSpatialVisionSequence,
  testSpatialVisionCondition,
  testSpatialVisionStep,
  updateSpatialVisionCondition,
  updateSpatialVisionReferenceImageMarkers,
  updateSpatialVisionSequence,
  updateSpatialVisionSettings,
  updateSpatialVisionStep,
  uploadSpatialVisionImage,
} from '@api/spatialVision.js';
import { spatialVisionDataChanged } from '@signals/spatialVision';
import _State from '@states/_State.js';
import {
  hasNewActiveJob,
  jobsPollPatch,
  nextJobPollDelayMs,
} from '@states/spatialVisionPolling.js';
import {
  clearHiddenDetectionKeysForItem,
  normalizeOverlayColor,
  stableOverlayColor,
  setHiddenDetectionKeys,
  toggleHiddenDetectionKey,
} from '@utils/spatialVisionOverlay.js';
import { normalizeVlmServiceTier } from '../pages/SpatialVision/utils/providerCatalog.js';
import {
  normalizeRunSettings,
  runnableSettings,
} from '../pages/SpatialVision/utils/runSettingsCatalog.js';
import {
  nextSequenceSelection,
  selectionForJob,
} from '../pages/SpatialVision/utils/sequenceSelection.js';
import { normalizeRois } from '../pages/SpatialVision/utils/roiGeometry.js';
import {
  conditionRoiUpdates,
  nextMarkId,
  withoutMark,
} from '../pages/SpatialVision/utils/marksGeometry.js';
import { SIMPLE_EDITOR } from '../pages/SpatialVision/utils/editorMode.js';
import {
  angleMarker,
  calibrationWithout,
  draftsFromImages,
  hasMarkerDraft,
  markerFromShape,
  markerKindOf,
  withoutMarker,
} from '../pages/SpatialVision/utils/markersGeometry.js';
import {
  DEFAULT_INSPECTOR_CONTEXT,
  normalizeInspectorContext,
} from '../pages/SpatialVision/utils/inspectorContexts.js';
import { isVideoFile } from '../pages/SpatialVision/utils/videoTracks.js';

const initialValue = {
  activeTab: 'prompt',
  loading: false,
  error: null,
  selectedServiceTier: 'cloud',
  selectedInspectorContext: DEFAULT_INSPECTOR_CONTEXT,
  runSettings: {},
  vlmVersions: [],
  roiTool: null,
  roiDraft: null,
  previewFiles: [],
  previewIndex: null,
  drawTarget: 'roi',
  // The condition editor is open: the canvas shows the marks palette on the image.
  conditionEditing: false,
  // The behaviour of the condition being edited: a Conveyor draft makes the photo the
  // Jetson's square before it is saved.
  conditionBehaviour: null,
  // A new sequence asks the sidebar to open the condition form once (a timestamp).
  conditionDraftRequest: null,
  calibrationDraft: null,
  // What the canvas shows: the photo, or one reference image of the condition being
  // edited. Marker drafts live per reference image so switching loses nothing.
  canvasSubject: { kind: 'frame' },
  referenceMarkerDrafts: {},
  referenceCalibrationDrafts: {},
  sequences: [],
  sessions: [],
  settings: {
    serverIp: 'http://127.0.0.1',
    serverPort: '9000',
    username: '',
    password: '',
    paymentStatus: 'active',
  },
  selectedSequenceId: null,
  selectedStepId: null,
  sequenceExpanded: true,
  conditionsExpanded: true,
  showSequenceList: false,
  showHistory: false,
  selectedSessionId: null,
  selectedDetectionIds: [],
  hiddenDetectionKeysByItem: {},
  jobs: [],
  sequenceProgressById: {},
  activeJobId: null,
  selectedJobItemId: null,
  showBBoxes: true,
  showLabels: false,
  showProBBoxes: false,
  showProLabels: false,
  showProSegmentation: true,
  showSegmentation: false,
  canvasZoom: 100,
  canvasPanX: 0,
  canvasPanY: 0,
  leftSidebarCollapsed: false,
  rightSidebarCollapsed: false,
  promptComposerCollapsed: false,
  rightTab: 'results',
};

// Matches the backend limit; a sampled video arrives as one job of up to this many frames.
const MAX_BATCH_IMAGES = 200;
const fallbackDetectionColor = (detection, context = {}) => {
  const key =
    detection.stepIndex ??
    detection.step_index ??
    context.stepIndex ??
    detection.stepId ??
    detection.step_id ??
    detection.step?.id ??
    context.stepId ??
    detection.id ??
    detection.title ??
    detection.description;
  return stableOverlayColor(key);
};

const state = _State('spatialVision', 'setSpatialVision', initialValue);
let sessionPollTimer = null;
let jobPollTimer = null;
let jobPollStartedAt = 0;
let jobPollJobIds = [];

const normalizeReferenceImage = condition => {
  const first = condition.referenceImages?.[0] || condition.referenceImage;
  return first?.storageUrl || condition.referenceImage || null;
};

const normalizeCondition = condition => ({
  ...condition,
  mode: condition.mode || 'text-conversational',
  statusLabel: condition.statusLabel || condition.status_label || null,
  statusLabelColor: normalizeOverlayColor(
    condition.statusLabelColor || condition.status_label_color,
  ),
  referenceImage: normalizeReferenceImage(condition),
});

const normalizeStep = step => ({
  ...step,
  conditions: (step.conditions || []).map(normalizeCondition),
});

const normalizeSequence = sequence => ({
  ...sequence,
  description: sequence.description || '',
  steps: (sequence.steps || sequence.captures || []).map(normalizeStep),
});

const normalizeDetection = detection => ({
  ...detection,
  title: detection.title || detection.step?.name || 'Detection',
  description: detection.description || 'Vision result',
  resultKind: String(detection.abnormalityType || '').startsWith('assessment:')
    ? 'assessment'
    : String(detection.abnormalityType || '').startsWith('check:')
      ? 'check'
      : 'detection',
  status: String(detection.abnormalityType || '').includes(':')
    ? String(detection.abnormalityType).split(':')[1]
    : null,
  abnormalityType:
    detection.abnormalityType || detection.type || 'Detected object',
  area:
    detection.area ||
    `${detection.width || detection.coordinates?.width || 0} x ${
      detection.height || detection.coordinates?.height || 0
    } px`,
  coordX: detection.coordX ?? detection.coordinates?.x ?? 0,
  coordY: detection.coordY ?? detection.coordinates?.y ?? 0,
  width: detection.width ?? detection.coordinates?.width ?? 0,
  height: detection.height ?? detection.coordinates?.height ?? 0,
  colorHex:
    detection.colorHex || detection.color || fallbackDetectionColor(detection),
  inputImageUrl: detection.inputImageUrl || detection.inputImage || null,
  outputImageUrl: detection.outputImageUrl || detection.outputImage || null,
});

const normalizeSession = session => {
  const detections = (session.detections || []).map(normalizeDetection);
  const resultMetadata =
    session.resultMetadata || session.result_metadata || session.metadata || {};
  return {
    ...session,
    resultMetadata,
    batchId: session.batchId || session.batch_id || resultMetadata.batchId,
    batchIndex:
      session.batchIndex ?? session.batch_index ?? resultMetadata.batchIndex,
    batchTotal:
      session.batchTotal ?? session.batch_total ?? resultMetadata.batchTotal,
    isCanceled:
      session.status === 'canceled' || Boolean(resultMetadata.canceled),
    serviceTier:
      session.serviceTier ||
      resultMetadata.serviceTier ||
      session.request?.serviceTier ||
      'cloud',
    timestamp: session.startedAt || session.createdAt || session.timestamp,
    prompt:
      session.prompt ||
      session.userPrompt ||
      session.inputPrompt ||
      session.request?.prompt ||
      '',
    inputImageUrl:
      session.inputImageUrl ||
      session.imageUrl ||
      session.request?.imageUrl ||
      detections.find(detection => detection.inputImageUrl)?.inputImageUrl ||
      null,
    llmOutput:
      resultMetadata.finalAnswer ||
      session.llmOutput ||
      session.response ||
      session.result ||
      session.summary ||
      '',
    detections,
  };
};

const truthyMetadataFlag = value => {
  if (typeof value === 'string') {
    return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

const normalizeJobDetection = (detection, context = {}) => {
  const width = Number(detection.width || 0);
  const height = Number(detection.height || 0);
  const label =
    detection.abnormalityType ||
    detection.title ||
    detection.description ||
    'Detection';
  const stepId =
    detection.stepId ??
    detection.step_id ??
    detection.step?.id ??
    context.stepId ??
    null;
  const stepIndex =
    detection.stepIndex ?? detection.step_index ?? context.stepIndex ?? null;
  const stepName =
    detection.stepName ||
    detection.step_name ||
    detection.step?.name ||
    context.stepName ||
    null;
  const color =
    detection.colorHex ||
    detection.color ||
    fallbackDetectionColor({ ...detection, stepId, stepIndex }, context);
  const confidenceValue =
    detection.modelEstimatedConfidence ??
    detection.model_estimated_confidence ??
    detection.confidence;
  // ?? NaN so an unknown confidence stays null on every pass: the stored null
  // would otherwise come back through Number() as 0 the next time the job is
  // merged, and the poll would see a change in a job that did not change.
  const confidence = Number(confidenceValue ?? NaN);
  return {
    ...detection,
    label,
    stepId,
    stepIndex,
    stepName,
    category: String(label).toLowerCase().includes('measure')
      ? 'dimension'
      : String(label).toLowerCase().includes('quality')
        ? 'quality'
        : 'defect',
    confidence: Number.isFinite(confidence) ? confidence : null,
    color,
    colorHex: detection.colorHex || detection.color || color,
    segmentationStatus:
      detection.segmentationStatus ||
      detection.segmentation_status ||
      detection.mask?.validation?.status ||
      null,
    bbox: {
      x: Number(detection.coordX || 0),
      y: Number(detection.coordY || 0),
      w: width,
      h: height,
    },
  };
};

const CONDITION_RESULT_STATUSES = new Set([
  'matched',
  'not_matched',
  'uncertain',
]);

const normalizeConditionResultStatus = status => {
  const value = String(status || '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_');
  if (CONDITION_RESULT_STATUSES.has(value)) return value;
  if (['match', 'yes', 'true', 'pass', 'passed'].includes(value))
    return 'matched';
  if (
    ['not_match', 'notmatched', 'no', 'false', 'fail', 'failed'].includes(value)
  )
    return 'not_matched';
  return 'uncertain';
};

const normalizeRelatedDetectionIndexes = (indexes, detectionCount = 0) => {
  if (!Array.isArray(indexes)) return [];
  return indexes
    .map(index => Number(index))
    .filter(
      (index, position, values) =>
        Number.isInteger(index) &&
        index >= 0 &&
        index < detectionCount &&
        values.indexOf(index) === position,
    );
};

const normalizeConditionResult = (result, context = {}) => ({
  ...result,
  conditionId: result.conditionId || result.condition_id || null,
  conditionName:
    result.conditionName ||
    result.condition_name ||
    result.name ||
    `Condition ${Number(context.index || 0) + 1}`,
  mode: result.mode || 'text-conversational',
  statusLabel: result.statusLabel || result.status_label || null,
  statusLabelColor: normalizeOverlayColor(
    result.statusLabelColor || result.status_label_color,
  ),
  resultStatus: normalizeConditionResultStatus(
    result.resultStatus || result.result_status,
  ),
  answer: result.answer || '',
  rationale: result.rationale || '',
  stepId: result.stepId || result.step_id || context.stepId || null,
  stepIndex: result.stepIndex ?? result.step_index ?? context.stepIndex ?? null,
  stepName: result.stepName || result.step_name || context.stepName || null,
  relatedDetectionIndexes: normalizeRelatedDetectionIndexes(
    result.relatedDetectionIndexes || result.related_detection_indexes,
    context.detectionCount || 0,
  ),
});

const normalizeJobItem = item => {
  const resultMetadata =
    item.resultMetadata || item.result_metadata || item.metadata || {};
  const stepId = item.stepId ?? item.step_id ?? item.step?.id ?? null;
  const stepIndex = item.stepIndex ?? item.step_index ?? null;
  const stepName =
    item.stepName ||
    item.step_name ||
    item.step?.name ||
    item.captureName ||
    null;
  const detections = (item.detections || []).map(detection =>
    normalizeJobDetection(detection, { stepId, stepIndex, stepName }),
  );
  return {
    ...item,
    imageId: item.id,
    url: item.imageUrl || item.url,
    name:
      item.filename || item.name || `Image ${Number(item.itemIndex || 0) + 1}`,
    itemIndex: item.itemIndex ?? item.item_index ?? 0,
    stepIndex,
    stepId,
    stepName,
    status: item.status === 'queued' ? 'running' : item.status,
    resultMetadata,
    llmOutput:
      resultMetadata.finalAnswer ||
      item.llmOutput ||
      item.llm_output ||
      item.summary ||
      '',
    inspectionStatus:
      resultMetadata.inspectionStatus || item.inspectionStatus || null,
    inspectionRationale:
      resultMetadata.inspectionRationale || item.inspectionRationale || '',
    modelNeedsRefinement: truthyMetadataFlag(
      resultMetadata.modelNeedsRefinement,
    ),
    modelRefinementReason: resultMetadata.modelRefinementReason || '',
    detections,
    conditionResults: (
      resultMetadata.conditionResults ||
      resultMetadata.condition_results ||
      item.conditionResults ||
      item.condition_results ||
      []
    ).map((result, index) =>
      normalizeConditionResult(result, {
        index,
        stepId,
        stepIndex,
        stepName,
        detectionCount: detections.length,
      }),
    ),
  };
};
const terminalStatuses = ['completed', 'failed', 'canceled'];
const activeStatuses = ['pending', 'queued', 'running'];

const normalizeItemStatus = status =>
  status === 'queued' ? 'running' : status;

const aggregateStatuses = statuses => {
  if (!statuses.length) return 'pending';
  const normalized = statuses.map(normalizeItemStatus);
  if (normalized.some(status => activeStatuses.includes(status)))
    return 'running';
  if (normalized.every(status => status === 'completed')) return 'completed';
  if (normalized.every(status => status === 'canceled')) return 'canceled';
  if (normalized.some(status => status === 'failed')) return 'failed';
  if (normalized.every(status => terminalStatuses.includes(status)))
    return 'partial';
  return 'pending';
};

const groupSequenceJobItems = (job, items) => {
  const byImage = new Map();
  items.forEach(item => {
    const key = String(item.itemIndex ?? 0);
    if (!byImage.has(key)) byImage.set(key, []);
    byImage.get(key).push(item);
  });

  const imageResults = [...byImage.values()]
    .map(group => {
      const sorted = [...group].sort(
        (a, b) => Number(a.stepIndex ?? 0) - Number(b.stepIndex ?? 0),
      );
      const first = sorted[0];
      const detections = sorted.flatMap(item => item.detections || []);
      let detectionOffset = 0;
      const conditionResults = sorted.flatMap(item => {
        const mapped = (item.conditionResults || []).map(result => ({
          ...result,
          relatedDetectionIndexes: (result.relatedDetectionIndexes || []).map(
            index => index + detectionOffset,
          ),
        }));
        detectionOffset += item.detections?.length || 0;
        return mapped;
      });
      return {
        ...first,
        id: first.id,
        imageId: first.id,
        itemIds: sorted.map(item => item.id),
        url: first.imageUrl || first.url,
        name:
          first.filename ||
          first.name ||
          `Image ${Number(first.itemIndex || 0) + 1}`,
        status: aggregateStatuses(sorted.map(item => item.status)),
        detections,
        conditionResults,
        detectionCount: detections.length,
        stepResults: sorted.map(item => ({
          ...item,
          status: normalizeItemStatus(item.status),
        })),
      };
    })
    .sort((a, b) => Number(a.itemIndex || 0) - Number(b.itemIndex || 0));

  const byStep = new Map();
  items.forEach(item => {
    const key = String(item.stepIndex ?? 0);
    if (!byStep.has(key)) byStep.set(key, []);
    byStep.get(key).push(item);
  });
  const stepResults = [...byStep.entries()]
    .map(([stepIndex, group]) => {
      const first = group[0];
      const detections = group.flatMap(item => item.detections || []);
      return {
        stepIndex: Number(stepIndex),
        stepId: first.stepId,
        step: first.step,
        status: aggregateStatuses(group.map(item => item.status)),
        completedItems: group.filter(item => item.status === 'completed')
          .length,
        totalItems: group.length,
        detectionCount: detections.length,
        items: group,
      };
    })
    .sort((a, b) => a.stepIndex - b.stepIndex);

  return {
    ...job,
    items,
    imageResults,
    stepResults,
  };
};

const normalizeJob = job => ({
  ...(() => {
    const metadata = job.metadata || {};
    const normalized = {
      ...job,
      type: job.type || 'direct',
      status: job.status === 'queued' ? 'running' : job.status,
      serviceTier: job.serviceTier || metadata.serviceTier || 'cloud',
      // job.startTime keeps the moment a job was first seen. mergeJobs re-normalizes
      // the jobs already in state, so a payload with neither startedAt nor createdAt
      // would take a fresh timestamp on every merge and never look unchanged again.
      startTime:
        job.startedAt ||
        job.createdAt ||
        job.startTime ||
        new Date().toISOString(),
      endTime: job.completedAt || null,
    };
    const items = (job.items || job.imageResults || []).map(normalizeJobItem);
    if (
      normalized.type === 'sequence' &&
      items.some(
        item => item.stepIndex !== null && item.stepIndex !== undefined,
      )
    ) {
      return groupSequenceJobItems(normalized, items);
    }
    return {
      ...normalized,
      items,
      imageResults: items,
      stepResults: [],
    };
  })(),
});
const deriveSequenceProgress = (
  jobs,
  sequences = state.spatialVision.sequences,
) => {
  const byId = {};
  (sequences || []).forEach(sequence => {
    const sequenceJobs = (jobs || [])
      .filter(job => String(job.sequenceId || '') === String(sequence.id))
      .sort(
        (a, b) =>
          new Date(b.startTime || b.startedAt || 0) -
          new Date(a.startTime || a.startedAt || 0),
      );
    const job =
      sequenceJobs.find(item => activeStatuses.includes(item.status)) ||
      sequenceJobs[0] ||
      null;
    const steps = sequence.steps || [];
    const stepResults = job?.stepResults || [];
    const statusByStepId = {};
    steps.forEach((step, index) => {
      const result =
        stepResults.find(item => String(item.stepId) === String(step.id)) ||
        stepResults.find(item => Number(item.stepIndex) === index);
      statusByStepId[step.id] = result?.status || 'pending';
    });
    const completedSteps = steps.filter(
      step => statusByStepId[step.id] === 'completed',
    ).length;
    const runningStep =
      steps.find(step => statusByStepId[step.id] === 'running') || null;
    byId[sequence.id] = {
      jobId: job?.id || null,
      status: job?.status || 'idle',
      completedSteps,
      totalSteps: steps.length,
      runningStepId: runningStep?.id || null,
      steps: statusByStepId,
    };
  });
  return byId;
};

const patch = value =>
  state.setSpatialVision({ ...state.spatialVision, ...value });

const mergeSessions = (incoming, existing = state.spatialVision.sessions) => {
  const byId = new Map();
  [...existing, ...incoming].forEach(session => {
    if (!session?.id) return;
    byId.set(String(session.id), session);
  });
  return [...byId.values()].sort(
    (a, b) =>
      new Date(b.startedAt || b.timestamp || 0) -
      new Date(a.startedAt || a.timestamp || 0),
  );
};

const markSessionsCanceled = predicate =>
  state.spatialVision.sessions.map(session => {
    if (!predicate(session)) return session;
    return {
      ...session,
      status: 'canceled',
      isCanceled: true,
      completedAt: session.completedAt || new Date().toISOString(),
      llmOutput: session.llmOutput || 'Canceled by user.',
      resultMetadata: {
        ...(session.resultMetadata || {}),
        canceled: true,
        canceledAt:
          session.resultMetadata?.canceledAt || new Date().toISOString(),
      },
    };
  });

const scheduleSessionPolling = sessions => {
  if (sessionPollTimer) {
    clearTimeout(sessionPollTimer);
    sessionPollTimer = null;
  }

  if (
    !sessions.some(
      session => session.status === 'running' || session.status === 'partial',
    )
  )
    return;

  sessionPollTimer = setTimeout(() => {
    state.refreshSessions().catch(error => {
      console.error('Failed to refresh Spatial Vision sessions', error);
    });
  }, 3000);
};

const scheduleJobPolling = jobs => {
  if (jobPollTimer) {
    clearTimeout(jobPollTimer);
    jobPollTimer = null;
  }

  const activeJobs = jobs.filter(job =>
    ['pending', 'queued', 'running'].includes(job.status),
  );
  if (!activeJobs.length) {
    jobPollStartedAt = 0;
    jobPollJobIds = [];
    return;
  }

  // Elapsed wall time rather than a tick count, and restarted by a job that was
  // not here last tick: a 1.2 s photo job queued beside a running video would
  // otherwise be polled once a second and finish before the second poll.
  const activeIds = activeJobs.map(job => String(job.id));
  if (hasNewActiveJob(jobPollJobIds, activeIds)) jobPollStartedAt = Date.now();
  jobPollJobIds = activeIds;

  const delay = nextJobPollDelayMs(Date.now() - jobPollStartedAt);
  jobPollTimer = setTimeout(() => {
    state.refreshJobs({ activeOnly: true }).catch(error => {
      console.error('Failed to refresh Spatial Vision jobs', error);
    });
  }, delay);
};

const mergeJobs = (incoming, existing = state.spatialVision.jobs) => {
  const byId = new Map();
  [...existing, ...incoming].forEach(job => {
    if (!job?.id) return;
    byId.set(String(job.id), normalizeJob(job));
  });
  return [...byId.values()].sort(
    (a, b) =>
      new Date(b.startedAt || b.startTime || 0) -
      new Date(a.startedAt || a.startTime || 0),
  );
};

const withLoading = async action => {
  await patch({ loading: true, error: null });
  try {
    const result = await action();
    await patch({ loading: false });
    return result;
  } catch (error) {
    await patch({
      loading: false,
      error: error.message || error.response || error,
    });
    throw error;
  }
};

const getSelectedSequence = () =>
  state.spatialVision.sequences.find(
    sequence =>
      String(sequence.id) === String(state.spatialVision.selectedSequenceId),
  );

const getSelectedStep = () =>
  getSelectedSequence()?.steps?.find(
    step => String(step.id) === String(state.spatialVision.selectedStepId),
  );

const replaceSequence = async sequence => {
  const sequences = state.spatialVision.sequences.map(item =>
    String(item.id) === String(sequence.id)
      ? normalizeSequence(sequence)
      : item,
  );
  await patch({
    sequences,
    sequenceProgressById: deriveSequenceProgress(
      state.spatialVision.jobs,
      sequences,
    ),
  });
};

const refreshSequence = async sequenceId => {
  const sequence = normalizeSequence(
    await getSpatialVisionSequence(sequenceId),
  );
  await replaceSequence(sequence);
  return sequence;
};

state.load = () =>
  withLoading(async () => {
    let sequences = (await listSpatialVisionSequences()).map(normalizeSequence);
    if (sequences.length) {
      sequences = await Promise.all(
        sequences.map(async sequence =>
          normalizeSequence(await getSpatialVisionSequence(sequence.id)),
        ),
      );
    }

    const selectedSequence = sequences[0] || null;
    const selectedStep = selectedSequence?.steps?.[0] || null;
    const sessionsResult = await listSpatialVisionSessions({ limit: 20 }).catch(
      () => ({
        rows: [],
      }),
    );
    const jobsResult = await listSpatialVisionJobs({ limit: 20 }).catch(() => ({
      rows: [],
    }));
    const settings = await getSpatialVisionSettings().catch(
      () => initialValue.settings,
    );
    const vlmVersions = await getSpatialVisionVlmVersions().catch(() => []);

    const sessions = (sessionsResult.rows || sessionsResult || []).map(
      normalizeSession,
    );
    const jobs = (jobsResult.rows || jobsResult || []).map(normalizeJob);
    const activeJob = jobs[0] || null;
    const activeJobItem = activeJob?.imageResults?.[0] || null;
    await patch({
      sequences,
      sessions,
      sequenceProgressById: deriveSequenceProgress(jobs, sequences),
      jobs,
      settings: { ...initialValue.settings, ...settings, password: '' },
      runSettings: normalizeRunSettings(settings.runSettings),
      vlmVersions,
      selectedSequenceId: selectedSequence?.id ?? null,
      selectedStepId: selectedStep?.id ?? null,
      selectedSessionId: sessions[0]?.id ?? null,
      activeJobId: activeJob?.id ?? null,
      selectedJobItemId: activeJobItem?.id ?? null,
    });
    scheduleSessionPolling(sessions);
    scheduleJobPolling(jobs);
  });

state.selectTab = activeTab => patch({ activeTab });
state.selectServiceTier = serviceTier => {
  const selectedServiceTier = normalizeVlmServiceTier(serviceTier);
  const { runSettings, vlmVersions } = state.spatialVision;
  const runnable = runnableSettings(
    runSettings,
    vlmVersions,
    selectedServiceTier,
  );
  return patch({
    selectedServiceTier,
    ...(runnable.vlmVersion !== runSettings.vlmVersion
      ? { runSettings: { ...runSettings, vlmVersion: undefined } }
      : {}),
  });
};
// Attached files, previewable on the canvas until a job is selected or started.
state.setPreviewFiles = files => {
  const previewFiles = Array.from(files || []);
  const current = state.spatialVision.previewIndex;
  const previewIndex = previewFiles.length
    ? Math.min(current ?? 0, previewFiles.length - 1)
    : null;
  return patch({ previewFiles, previewIndex });
};
state.setPreview = index =>
  patch({ previewIndex: index, canvasSubject: { kind: 'frame' } });
state.setRoiTool = (roiTool, drawTarget = 'roi') =>
  patch({ roiTool: roiTool || null, drawTarget: roiTool ? drawTarget : 'roi' });
state.setConditionEditing = (conditionEditing, behaviour = null) =>
  patch({
    conditionEditing: Boolean(conditionEditing),
    conditionBehaviour: conditionEditing ? behaviour : null,
  });
state.requestConditionDraft = () =>
  patch({ conditionDraftRequest: Date.now() });
state.setRoiDraft = roiDraft => patch({ roiDraft: normalizeRois(roiDraft) });
state.addRoiDraft = shape =>
  state.setRoiDraft([...(state.spatialVision.roiDraft || []), shape]);
state.setRoiDraftMode = (index, mode) =>
  state.setRoiDraft(
    (state.spatialVision.roiDraft || []).map((shape, i) =>
      i === index ? { ...shape, mode } : shape,
    ),
  );
state.removeRoiDraft = index =>
  state.setRoiDraft(
    (state.spatialVision.roiDraft || []).filter((_, i) => i !== index),
  );

const subjectImageId = () => {
  const subject = state.spatialVision.canvasSubject;
  return subject?.kind === 'reference' ? String(subject.imageId) : null;
};
state.setCanvasSubject = subject =>
  patch({
    canvasSubject: subject?.kind === 'reference' ? subject : { kind: 'frame' },
    roiTool: null,
    drawTarget: 'roi',
  });
state.seedReferenceDrafts = images => patch(draftsFromImages(images));
state.setReferenceMarkers = (imageId, markers) =>
  patch({
    referenceMarkerDrafts: {
      ...state.spatialVision.referenceMarkerDrafts,
      [imageId]: markers,
    },
  });
state.addMarkerDraft = (kind, shape) => {
  const imageId = subjectImageId();
  if (!imageId) return undefined;
  const markers = state.spatialVision.referenceMarkerDrafts[imageId] || [];
  return state.setReferenceMarkers(imageId, [
    ...markers,
    markerFromShape(kind, shape, markers),
  ]);
};
state.updateMarkerDraft = (markerId, fields) => {
  const imageId = subjectImageId();
  if (!imageId) return undefined;
  return state.setReferenceMarkers(
    imageId,
    (state.spatialVision.referenceMarkerDrafts[imageId] || []).map(marker =>
      marker.id === markerId ? { ...marker, ...fields } : marker,
    ),
  );
};
state.removeMarkerDraft = markerId => {
  const imageId = subjectImageId();
  if (!imageId) return undefined;
  const { referenceMarkerDrafts, referenceCalibrationDrafts } =
    state.spatialVision;
  return patch({
    referenceMarkerDrafts: {
      ...referenceMarkerDrafts,
      [imageId]: withoutMarker(referenceMarkerDrafts[imageId], markerId),
    },
    referenceCalibrationDrafts: {
      ...referenceCalibrationDrafts,
      [imageId]: calibrationWithout(
        referenceCalibrationDrafts[imageId],
        markerId,
      ),
    },
  });
};
state.addAngleDraft = (lineA, lineB) => {
  const imageId = subjectImageId();
  if (!imageId) return undefined;
  const markers = state.spatialVision.referenceMarkerDrafts[imageId] || [];
  const marker = angleMarker(markers, lineA, lineB);
  if (!marker) throw new Error('Pick two different lines');
  return state.setReferenceMarkers(imageId, [...markers, marker]);
};
state.setReferenceCalibrationDraft = fields => {
  const imageId = subjectImageId();
  if (!imageId) return undefined;
  const drafts = state.spatialVision.referenceCalibrationDrafts;
  return patch({
    referenceCalibrationDrafts: {
      ...drafts,
      [imageId]: { ...(drafts[imageId] || {}), ...fields },
    },
  });
};
// draftKey differs from imageId for a file drawn on before its first upload: the
// draft lived under the pending key and is saved to the row the upload created.
state.saveReferenceMarkers = (conditionId, imageId, draftKey = imageId) =>
  withLoading(async () => {
    const { referenceMarkerDrafts, referenceCalibrationDrafts } =
      state.spatialVision;
    await updateSpatialVisionReferenceImageMarkers(conditionId, imageId, {
      markers: referenceMarkerDrafts[draftKey] || [],
      calibration: referenceCalibrationDrafts[draftKey] || null,
    });
    await refreshSequence(getSelectedSequence().id);
  });
state.hasReferenceDraft = key =>
  hasMarkerDraft(
    state.spatialVision.referenceMarkerDrafts[key],
    state.spatialVision.referenceCalibrationDrafts[key],
  );
state.dropReferenceDraft = key => {
  const { referenceMarkerDrafts, referenceCalibrationDrafts } =
    state.spatialVision;
  const { [key]: _markers, ...markers } = referenceMarkerDrafts;
  const { [key]: _calibration, ...calibrations } = referenceCalibrationDrafts;
  return patch({
    referenceMarkerDrafts: markers,
    referenceCalibrationDrafts: calibrations,
  });
};

// One drawing gesture on the canvas feeds the ROI editor, a step mark, or a
// calibration point, depending on which control started it. On a reference image
// every gesture is a marker of that image, whichever button started it. A step mark
// keeps the size of the photo it was drawn on (frame): a Conveyor export carries it
// from there into the Jetson's view.
state.commitDraw = async (shape, frame) => {
  const { drawTarget, calibrationDraft, canvasSubject } = state.spatialVision;
  await patch({ roiTool: null, drawTarget: 'roi' });
  if (canvasSubject?.kind === 'reference') {
    return state.addMarkerDraft(markerKindOf(shape), shape);
  }
  if (drawTarget === 'roi') return state.addRoiDraft(shape);
  const drawnOn = frame ? { frame } : {};
  if (drawTarget === 'area')
    return state.addMark({ kind: 'area', shape, ...drawnOn });
  if (drawTarget === 'line')
    return state.addMark({ kind: 'line', shape, ...drawnOn });
  if (drawTarget === 'point')
    return state.addMark({ kind: 'point', shape, ...drawnOn });
  if (drawTarget === 'r1' || drawTarget === 'r2') {
    const sequence = getSelectedSequence();
    const item = state.spatialVision.jobs
      .find(job => String(job.id) === String(state.spatialVision.activeJobId))
      ?.imageResults?.find(
        candidate =>
          String(candidate.id) ===
          String(state.spatialVision.selectedJobItemId),
      );
    return patch({
      calibrationDraft: {
        ...(calibrationDraft || sequence?.calibration || {}),
        [drawTarget]: shape,
        imageUrl: item?.url || calibrationDraft?.imageUrl || null,
      },
    });
  }
  return undefined;
};
state.setCalibrationDraft = fields =>
  patch({
    calibrationDraft: {
      ...(state.spatialVision.calibrationDraft ||
        getSelectedSequence()?.calibration ||
        {}),
      ...fields,
    },
  });
state.saveCalibration = () =>
  withLoading(async () => {
    const sequence = getSelectedSequence();
    const draft = state.spatialVision.calibrationDraft;
    if (
      !sequence ||
      !draft?.r1 ||
      !draft?.r2 ||
      !(Number(draft.distanceMm) > 0)
    ) {
      throw new Error('Set R1, R2, and the distance between them in mm');
    }
    // The update response is the bare sequence row; the steps only come back from a fetch.
    await updateSpatialVisionSequence(sequence.id, {
      calibration: { ...draft, distanceMm: Number(draft.distanceMm) },
    });
    await refreshSequence(sequence.id);
    await patch({ calibrationDraft: null });
    spatialVisionDataChanged.dispatch();
  });
state.clearCalibration = () =>
  withLoading(async () => {
    const sequence = getSelectedSequence();
    if (!sequence) return;
    await updateSpatialVisionSequence(sequence.id, { calibration: null });
    await refreshSequence(sequence.id);
    await patch({ calibrationDraft: null });
    spatialVisionDataChanged.dispatch();
  });
// Conditions saved by the old build are still filtering by a copy of the step's area
// marks, and nothing in the simple editor can take it off. Every change to the marks
// passes through here, so the leftovers are cleared here, before the sequence is read
// back and the canvas redrawn once.
state.updateMarks = marks => {
  const sequence = getSelectedSequence();
  const step = getSelectedStep();
  if (!sequence || !step) throw new Error('Select a step first');
  const stale = SIMPLE_EDITOR
    ? conditionRoiUpdates(step.conditions, step.marks)
    : [];
  return withLoading(async () => {
    await updateSpatialVisionStep(sequence.id, step.id, { marks });
    await Promise.all(
      stale.map(({ id, roi }) =>
        updateSpatialVisionCondition(step.id, id, { roi }),
      ),
    );
    await refreshSequence(sequence.id);
    spatialVisionDataChanged.dispatch();
  });
};
state.addMark = mark => {
  const marks = getSelectedStep()?.marks || [];
  return state.updateMarks([
    ...marks,
    {
      label: '',
      type: '',
      description: '',
      ...mark,
      id: nextMarkId(marks, mark.kind),
    },
  ]);
};
state.addAngleMark = (lineA, lineB) => {
  const marks = getSelectedStep()?.marks || [];
  const lines = marks.filter(mark => mark.kind === 'line').map(mark => mark.id);
  if (lineA === lineB || !lines.includes(lineA) || !lines.includes(lineB)) {
    throw new Error('Pick two different lines on the photo');
  }
  return state.addMark({ kind: 'angle', lines: [lineA, lineB] });
};
state.updateMark = (markId, fields) =>
  state.updateMarks(
    (getSelectedStep()?.marks || []).map(mark =>
      mark.id === markId ? { ...mark, ...fields } : mark,
    ),
  );
state.deleteMark = markId => {
  const step = getSelectedStep();
  const used = (step?.conditions || []).some(
    condition =>
      condition.measurement?.from === markId ||
      condition.measurement?.to === markId,
  );
  if (used) throw new Error(`${markId} is used by a measurement condition`);
  return state.updateMarks(
    withoutMark(step?.marks, markId).filter(mark => mark.id !== markId),
  );
};
let runSettingsSaveVersion = 0;
state.setRunSetting = async (key, value) => {
  const version = ++runSettingsSaveVersion;
  const runSettings = normalizeRunSettings({
    ...state.spatialVision.runSettings,
    [key]: value,
  });
  await patch({ runSettings });
  const saved = await updateSpatialVisionSettings({ runSettings });
  // Every save carries the full object, so a change made while this request was
  // in flight has its own request whose reply supersedes this one. Applying this
  // reply anyway would drop that newer change from the state.
  if (version !== runSettingsSaveVersion) return;
  await patch({ runSettings: normalizeRunSettings(saved?.runSettings) });
};
state.selectSequence = sequenceId => {
  const nextId = nextSequenceSelection(
    state.spatialVision.selectedSequenceId,
    sequenceId,
  );
  const sequence = state.spatialVision.sequences.find(
    item => String(item.id) === String(nextId),
  );
  return patch({
    selectedSequenceId: nextId,
    selectedStepId: sequence?.steps?.[0]?.id ?? null,
    showSequenceList: false,
  });
};
state.selectStep = stepId => patch({ selectedStepId: stepId });
state.toggleSequenceList = () =>
  patch({ showSequenceList: !state.spatialVision.showSequenceList });
state.toggleSequencePanel = () =>
  patch({ sequenceExpanded: !state.spatialVision.sequenceExpanded });
state.toggleConditionsPanel = () =>
  patch({ conditionsExpanded: !state.spatialVision.conditionsExpanded });
state.toggleLeftSidebar = () =>
  patch({ leftSidebarCollapsed: !state.spatialVision.leftSidebarCollapsed });
state.toggleRightSidebar = () =>
  patch({ rightSidebarCollapsed: !state.spatialVision.rightSidebarCollapsed });
state.togglePromptComposer = () =>
  patch({
    promptComposerCollapsed: !state.spatialVision.promptComposerCollapsed,
  });
state.toggleHistory = () =>
  patch({ showHistory: !state.spatialVision.showHistory });
state.selectSession = sessionId =>
  patch({
    selectedSessionId: sessionId,
    showHistory: false,
    selectedDetectionIds: [],
  });

state.selectJob = jobId => {
  const job = state.spatialVision.jobs.find(
    item => String(item.id) === String(jobId),
  );
  return patch({
    ...selectionForJob(
      job,
      state.spatialVision.sequences,
      state.spatialVision.selectedSequenceId,
      state.spatialVision.selectedStepId,
    ),
    activeJobId: jobId,
    selectedJobItemId: job?.imageResults?.[0]?.id || null,
    previewIndex: null,
    showHistory: false,
    canvasZoom: 100,
    canvasPanX: 0,
    canvasPanY: 0,
  });
};

state.selectJobItem = itemId => {
  const selectedItemChanged =
    String(itemId || '') !==
    String(state.spatialVision.selectedJobItemId || '');
  return patch({
    selectedJobItemId: itemId,
    previewIndex: null,
    canvasSubject: { kind: 'frame' },
    ...(selectedItemChanged
      ? { canvasZoom: 100, canvasPanX: 0, canvasPanY: 0 }
      : {}),
  });
};
state.selectRightTab = rightTab => patch({ rightTab });
state.setCanvasOption = (key, value) => patch({ [key]: value });
state.setCanvasPan = (x, y) => patch({ canvasPanX: x, canvasPanY: y });
state.setCanvasView = (zoom, x, y) =>
  patch({ canvasZoom: zoom, canvasPanX: x, canvasPanY: y });
state.resetCanvasView = () =>
  patch({ canvasZoom: 100, canvasPanX: 0, canvasPanY: 0 });
state.toggleDetectionOverlay = (itemId, overlayKey) =>
  patch({
    hiddenDetectionKeysByItem: toggleHiddenDetectionKey(
      state.spatialVision.hiddenDetectionKeysByItem,
      itemId,
      overlayKey,
    ),
  });
state.setDetectionOverlays = (itemId, overlayKeys, hidden) =>
  patch({
    hiddenDetectionKeysByItem: setHiddenDetectionKeys(
      state.spatialVision.hiddenDetectionKeysByItem,
      itemId,
      overlayKeys,
      hidden,
    ),
  });
state.showAllDetectionOverlays = itemId =>
  patch({
    hiddenDetectionKeysByItem: clearHiddenDetectionKeysForItem(
      state.spatialVision.hiddenDetectionKeysByItem,
      itemId,
    ),
  });

let jobsRefreshVersion = 0;
state.refreshJobs = async ({ activeOnly = false } = {}) => {
  const version = ++jobsRefreshVersion;
  const sourceJobs = activeOnly
    ? state.spatialVision.jobs.filter(job =>
        ['pending', 'queued', 'running'].includes(job.status),
      )
    : [];
  const incoming = activeOnly
    ? await Promise.all(
        sourceJobs.map(job => getSpatialVisionJob(job.id).catch(() => job)),
      )
    : (await listSpatialVisionJobs({ limit: 20 })).rows || [];
  const jobs = mergeJobs(incoming);
  const stale = version !== jobsRefreshVersion;
  const selection = jobsPollPatch(
    state.spatialVision.jobs,
    jobs,
    state.spatialVision,
    stale,
  );
  if (selection) {
    await patch({
      jobs,
      sequenceProgressById: deriveSequenceProgress(jobs),
      ...selection,
    });
  }
  // Scheduled from every reply, stale or not: the list only picks the delay, and a
  // reply that skipped it could be the one that was going to set the next tick.
  scheduleJobPolling(jobs);
  return jobs;
};

state.runDirectJob = async ({ prompt, files, serviceTier, context }) =>
  withLoading(async () => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (!prompt?.trim()) throw new Error('Prompt is required');
    if (!selectedFiles.length) throw new Error('Input images are required');
    if (selectedFiles.length > MAX_BATCH_IMAGES) {
      throw new Error(`Select up to ${MAX_BATCH_IMAGES} images for one job`);
    }
    const images = await Promise.all(
      selectedFiles.map(async file => ({
        imageUrl: await uploadSpatialVisionImage(file, 'prompt'),
        filename: file.name,
        ...(isVideoFile(file) ? { kind: 'video' } : {}),
      })),
    );
    const job = normalizeJob(
      await createSpatialVisionDirectJob({
        prompt,
        images,
        serviceTier: serviceTier || state.spatialVision.selectedServiceTier,
        context: normalizeInspectorContext(
          context || state.spatialVision.selectedInspectorContext,
        ),
        geometryMode: 'bounding_box',
        runSettings: runnableSettings(
          normalizeRunSettings(state.spatialVision.runSettings),
          state.spatialVision.vlmVersions,
          state.spatialVision.selectedServiceTier,
        ),
      }),
    );
    const jobs = mergeJobs([job]);
    await patch({
      jobs,
      sequenceProgressById: deriveSequenceProgress(jobs),
      activeJobId: job.id,
      previewIndex: null,
      selectedJobItemId: job.imageResults?.[0]?.id || null,
      showHistory: false,
      canvasZoom: 100,
      canvasPanX: 0,
      canvasPanY: 0,
    });
    scheduleJobPolling(jobs);
    return job;
  });

state.runSequenceJob = async ({
  sequenceId,
  files,
  prompt,
  serviceTier,
  context,
}) =>
  withLoading(async () => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (!sequenceId) throw new Error('Sequence is required');
    const runningSameSequence = state.spatialVision.jobs.some(
      job =>
        job.type === 'sequence' &&
        String(job.sequenceId) === String(sequenceId) &&
        activeStatuses.includes(job.status),
    );
    if (runningSameSequence) {
      throw new Error('This sequence is already running');
    }
    if (!selectedFiles.length) throw new Error('Input images are required');
    if (selectedFiles.length > MAX_BATCH_IMAGES) {
      throw new Error(`Select up to ${MAX_BATCH_IMAGES} images for one job`);
    }
    const images = await Promise.all(
      selectedFiles.map(async file => ({
        imageUrl: await uploadSpatialVisionImage(file, 'prompt'),
        filename: file.name,
        ...(isVideoFile(file) ? { kind: 'video' } : {}),
      })),
    );
    const job = normalizeJob(
      await createSpatialVisionSequenceJob(sequenceId, {
        prompt,
        images,
        serviceTier: serviceTier || state.spatialVision.selectedServiceTier,
        context: normalizeInspectorContext(
          context || state.spatialVision.selectedInspectorContext,
        ),
        geometryMode: 'bounding_box',
        runSettings: runnableSettings(
          normalizeRunSettings(state.spatialVision.runSettings),
          state.spatialVision.vlmVersions,
          state.spatialVision.selectedServiceTier,
        ),
      }),
    );
    const jobs = mergeJobs([job]);
    await patch({
      jobs,
      sequenceProgressById: deriveSequenceProgress(jobs),
      activeJobId: job.id,
      previewIndex: null,
      selectedJobItemId: job.imageResults?.[0]?.id || null,
      showHistory: false,
      canvasZoom: 100,
      canvasPanX: 0,
      canvasPanY: 0,
    });
    scheduleJobPolling(jobs);
    return job;
  });

state.cancelJob = async jobId =>
  withLoading(async () => {
    const job = normalizeJob(await cancelSpatialVisionJob(jobId));
    const jobs = mergeJobs([job]);
    await patch({ jobs, sequenceProgressById: deriveSequenceProgress(jobs) });
    scheduleJobPolling(jobs);
    return job;
  });

state.cancelJobItem = async (jobId, itemId) =>
  withLoading(async () => {
    const job = normalizeJob(await cancelSpatialVisionJobItem(jobId, itemId));
    const jobs = mergeJobs([job]);
    await patch({ jobs, sequenceProgressById: deriveSequenceProgress(jobs) });
    scheduleJobPolling(jobs);
    return job;
  });

state.createSequence = (name, description = '') =>
  withLoading(async () => {
    const createdSequence = await createSpatialVisionSequence({
      name,
      description,
    });
    const sequence = normalizeSequence({
      ...createdSequence,
      description,
    });
    await patch({
      // newest first, as the list comes from the backend after a reload
      sequences: [sequence, ...state.spatialVision.sequences],
      sequenceProgressById: deriveSequenceProgress(state.spatialVision.jobs, [
        sequence,
        ...state.spatialVision.sequences,
      ]),
      selectedSequenceId: sequence.id,
      selectedStepId: null,
      showSequenceList: false,
    });
    spatialVisionDataChanged.dispatch();
    return sequence;
  });

state.createSequenceFromCurrent = (name, description = '') =>
  withLoading(async () => {
    const currentSequence = getSelectedSequence();
    if (!currentSequence) return state.createSequence(name, description);

    const createdSequenceResponse = await createSpatialVisionSequence({
      name,
      description,
    });
    const createdSequence = normalizeSequence({
      ...createdSequenceResponse,
      description,
    });

    const createdSteps = [];
    for (let index = 0; index < (currentSequence.steps || []).length; index++) {
      const sourceStep = currentSequence.steps[index];
      const createdStep = await createSpatialVisionStep(createdSequence.id, {
        name: sourceStep.name,
        description: sourceStep.description || '',
        context: normalizeInspectorContext(sourceStep.context, null),
        active: sourceStep.active !== undefined ? sourceStep.active : true,
        orderIndex: index,
      });

      const createdConditions = [];
      for (
        let conditionIndex = 0;
        conditionIndex < (sourceStep.conditions || []).length;
        conditionIndex++
      ) {
        const sourceCondition = sourceStep.conditions[conditionIndex];
        const createdCondition = await createSpatialVisionCondition(
          createdStep.id,
          {
            name: sourceCondition.name,
            type: sourceCondition.type,
            value: sourceCondition.value,
            orderIndex: conditionIndex,
          },
        );

        if (sourceCondition.referenceImage) {
          await createSpatialVisionReferenceImage(createdCondition.id, {
            imageUrl: sourceCondition.referenceImage,
            filename: sourceCondition.value || sourceCondition.name,
          });
        }

        createdConditions.push(
          normalizeCondition({
            ...createdCondition,
            referenceImage: sourceCondition.referenceImage,
          }),
        );
      }

      createdSteps.push(
        normalizeStep({
          ...createdStep,
          conditions: createdConditions,
        }),
      );
    }

    const sequence = normalizeSequence({
      ...createdSequence,
      steps: createdSteps,
    });
    await patch({
      // newest first, as the list comes from the backend after a reload
      sequences: [sequence, ...state.spatialVision.sequences],
      sequenceProgressById: deriveSequenceProgress(state.spatialVision.jobs, [
        sequence,
        ...state.spatialVision.sequences,
      ]),
      selectedSequenceId: sequence.id,
      selectedStepId: sequence.steps?.[0]?.id ?? null,
      showSequenceList: false,
    });
    spatialVisionDataChanged.dispatch();
    return sequence;
  });

state.renameSequence = (sequenceId, name) =>
  withLoading(async () => {
    await replaceSequence(
      await updateSpatialVisionSequence(sequenceId, { name }),
    );
    spatialVisionDataChanged.dispatch();
  });

state.deleteSequence = sequenceId =>
  withLoading(async () => {
    await deleteSpatialVisionSequence(sequenceId);
    const sequences = state.spatialVision.sequences.filter(
      item => String(item.id) !== String(sequenceId),
    );
    const next = sequences[0] || null;
    await patch({
      sequences,
      sequenceProgressById: deriveSequenceProgress(
        state.spatialVision.jobs,
        sequences,
      ),
      selectedSequenceId: next?.id ?? null,
      selectedStepId: next?.steps?.[0]?.id ?? null,
    });
    spatialVisionDataChanged.dispatch();
  });

state.createStep = (sequenceId, stepData) =>
  withLoading(async () => {
    const payload =
      typeof stepData === 'string'
        ? { name: stepData, description: 'New detection step' }
        : stepData;
    await createSpatialVisionStep(sequenceId, {
      name: payload.name,
      description: payload.description || '',
      context: normalizeInspectorContext(payload.context, null),
      active: payload.active !== undefined ? payload.active : true,
      orderIndex: getSelectedSequence()?.steps?.length || 0,
    });
    const sequence = await refreshSequence(sequenceId);
    await patch({
      selectedStepId:
        sequence.steps.at(-1)?.id ?? state.spatialVision.selectedStepId,
    });
    spatialVisionDataChanged.dispatch();
  });

state.updateStep = (sequenceId, stepId, payload) =>
  withLoading(async () => {
    await updateSpatialVisionStep(sequenceId, stepId, payload);
    await refreshSequence(sequenceId);
    spatialVisionDataChanged.dispatch();
  });

state.selectInspectorContext = context =>
  patch({ selectedInspectorContext: normalizeInspectorContext(context) });

state.deleteStep = (sequenceId, stepId) =>
  withLoading(async () => {
    await deleteSpatialVisionStep(sequenceId, stepId);
    const sequence = getSelectedSequence();
    const steps = sequence.steps.filter(
      step => String(step.id) !== String(stepId),
    );
    await replaceSequence({ ...sequence, steps });
    await patch({
      selectedStepId: steps[0]?.id ?? null,
      sequenceProgressById: deriveSequenceProgress(state.spatialVision.jobs),
    });
    spatialVisionDataChanged.dispatch();
  });

state.createCondition = (stepId, payload) =>
  withLoading(async () => {
    const sequence = getSelectedSequence();
    const condition = await createSpatialVisionCondition(stepId, {
      ...payload,
      orderIndex: getSelectedStep()?.conditions?.length || 0,
    });
    await refreshSequence(sequence.id);
    spatialVisionDataChanged.dispatch();
    return normalizeCondition(condition);
  });

state.updateCondition = (stepId, conditionId, payload) =>
  withLoading(async () => {
    const sequence = getSelectedSequence();
    const condition = await updateSpatialVisionCondition(
      stepId,
      conditionId,
      payload,
    );
    await refreshSequence(sequence.id);
    spatialVisionDataChanged.dispatch();
    return normalizeCondition(condition);
  });

state.deleteCondition = (stepId, conditionId) =>
  withLoading(async () => {
    const sequence = getSelectedSequence();
    await deleteSpatialVisionCondition(stepId, conditionId);
    await replaceSequence({
      ...sequence,
      steps: sequence.steps.map(step =>
        String(step.id) === String(stepId)
          ? {
              ...step,
              conditions: step.conditions.filter(
                condition => String(condition.id) !== String(conditionId),
              ),
            }
          : step,
      ),
    });
    spatialVisionDataChanged.dispatch();
  });

state.attachReferenceImage = (conditionId, file) =>
  withLoading(async () => {
    const imageUrl = await uploadSpatialVisionImage(file, 'reference');
    const created = await createSpatialVisionReferenceImage(conditionId, {
      imageUrl,
      filename: file.name,
    });
    await refreshSequence(getSelectedSequence().id);
    return created;
  });

// Many reference images at once (a Conveyor sequence can hold hundreds): uploaded REFERENCE_BATCH at a time, each
// tried up to REFERENCE_TRIES times, the sequence reloaded once at the end. Attaching them one by one reloaded the
// whole sequence and redrew every thumbnail after each photo, so each photo cost more than the last: about 10 s a
// photo on the Jetson's Chromium, where a 238-photo save stopped at 21 (2026-10-15). onProgress(done, total) after
// each photo; returns the files that still failed, so the caller can keep them for another Save.
const REFERENCE_BATCH = 3;
const REFERENCE_TRIES = 3;

const attachWithRetry = async (conditionId, file) => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      const imageUrl = await uploadSpatialVisionImage(file, 'reference');
      return await createSpatialVisionReferenceImage(conditionId, {
        imageUrl,
        filename: file.name,
      });
    } catch (error) {
      // a refusal (the condition deleted meanwhile: 404) stays a refusal; only a lost request is worth repeating
      if (attempt >= REFERENCE_TRIES || error.response?.status < 500)
        throw error;
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
};

state.attachReferenceImages = async (conditionId, files, keys, onProgress) => {
  const failed = [];
  let done = 0;
  for (let start = 0; start < files.length; start += REFERENCE_BATCH) {
    const batch = files.slice(start, start + REFERENCE_BATCH);
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(
      batch.map(async (file, offset) => {
        const index = start + offset;
        try {
          const created = await attachWithRetry(conditionId, file);
          const key = keys?.[index];
          // Markers drawn on a file before its first save follow it to its row.
          if (created?.id && key && state.hasReferenceDraft(key)) {
            await updateSpatialVisionReferenceImageMarkers(
              conditionId,
              String(created.id),
              {
                markers: state.spatialVision.referenceMarkerDrafts[key] || [],
                calibration:
                  state.spatialVision.referenceCalibrationDrafts[key] || null,
              },
            );
          }
        } catch (error) {
          failed.push({ index, file, error });
        }
        done += 1;
        onProgress?.(done, files.length);
      }),
    );
  }
  await refreshSequence(getSelectedSequence().id);
  return failed.sort((a, b) => a.index - b.index);
};

state.deleteReferenceImageById = (conditionId, imageId) =>
  withLoading(async () => {
    await deleteSpatialVisionReferenceImageById(conditionId, imageId);
    await refreshSequence(getSelectedSequence().id);
    spatialVisionDataChanged.dispatch();
  });

state.deleteReferenceImage = conditionId =>
  withLoading(async () => {
    await deleteSpatialVisionReferenceImage(conditionId);
    await refreshSequence(getSelectedSequence().id);
    spatialVisionDataChanged.dispatch();
  });

state.runChat = async (text, file) =>
  withLoading(async () => {
    if (!file) throw new Error('Input image is required');

    const imageUrl = await uploadSpatialVisionImage(file, 'prompt');
    const temporarySessionId = `quick-${Date.now()}`;
    const temporarySession = normalizeSession({
      id: temporarySessionId,
      status: 'running',
      prompt: text,
      inputImageUrl: imageUrl,
      serviceTier: state.spatialVision.selectedServiceTier,
      timestamp: new Date().toISOString(),
      sequence: null,
      sequenceId: null,
      detections: [],
    });

    await patch({
      sessions: [
        temporarySession,
        ...state.spatialVision.sessions.filter(
          session => String(session.id) !== String(temporarySessionId),
        ),
      ],
      selectedSessionId: temporarySessionId,
      showHistory: false,
    });

    try {
      const result = await runSpatialVisionChat({
        imageUrl,
        prompt: text,
        serviceTier: state.spatialVision.selectedServiceTier,
      });
      const returnedSession = result?.session
        ? normalizeSession(result.session)
        : null;

      if (!returnedSession) {
        await state.refreshSessions({ preferLatest: true });
        return null;
      }

      const sessions = [
        returnedSession,
        ...state.spatialVision.sessions.filter(
          session =>
            String(session.id) !== String(returnedSession.id) &&
            String(session.id) !== String(temporarySessionId),
        ),
      ];
      await patch({
        sessions,
        selectedSessionId: returnedSession.id,
        showHistory: false,
      });
      scheduleSessionPolling(sessions);
      return returnedSession;
    } catch (error) {
      const sessions = state.spatialVision.sessions.map(session =>
        String(session.id) === String(temporarySessionId)
          ? {
              ...session,
              status: 'failed',
              llmOutput: error.message || String(error),
            }
          : session,
      );
      await patch({ sessions, selectedSessionId: temporarySessionId });
      throw error;
    }
  });

state.runChatBatch = async (text, files) =>
  withLoading(async () => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (!text?.trim()) throw new Error('Prompt is required');
    if (!selectedFiles.length) throw new Error('Input images are required');
    if (selectedFiles.length > MAX_BATCH_IMAGES) {
      throw new Error(
        `Select up to ${MAX_BATCH_IMAGES} images for one batch request`,
      );
    }

    const uploads = await Promise.all(
      selectedFiles.map(async file => ({
        imageUrl: await uploadSpatialVisionImage(file, 'prompt'),
        filename: file.name,
      })),
    );
    const result = await runSpatialVisionChatBatch({
      prompt: text,
      serviceTier: state.spatialVision.selectedServiceTier,
      images: uploads,
    });
    const sessions = (result.sessions || []).map(normalizeSession);
    const mergedSessions = mergeSessions(sessions);
    await patch({
      sessions: mergedSessions,
      selectedSessionId:
        sessions[0]?.id ?? state.spatialVision.selectedSessionId,
      showHistory: false,
    });
    scheduleSessionPolling(mergedSessions);
    return {
      ...result,
      sessions,
    };
  });

state.runStep = (stepId, file, prompt) =>
  withLoading(async () => {
    if (!file) throw new Error('Input image is required');
    const imageUrl = await uploadSpatialVisionImage(file, 'step');
    const result = await testSpatialVisionStep(stepId, {
      imageUrl,
      prompt,
      serviceTier: state.spatialVision.selectedServiceTier,
    });
    await state.refreshSessions({ preferLatest: true });
    return result;
  });

state.runCondition = (conditionId, file, prompt) =>
  withLoading(async () => {
    if (!file) throw new Error('Input image is required');
    const imageUrl = await uploadSpatialVisionImage(file, 'condition');
    const result = await testSpatialVisionCondition(conditionId, {
      imageUrl,
      prompt,
      serviceTier: state.spatialVision.selectedServiceTier,
    });
    await state.refreshSessions({ preferLatest: true });
    return result;
  });

state.runSequence = (sequenceId, file, prompt) =>
  withLoading(async () => {
    if (!file) throw new Error('Input image is required');
    const imageUrl = await uploadSpatialVisionImage(file, 'sequence');
    const result = await runSpatialVisionSequence(sequenceId, {
      imageUrl,
      prompt,
      serviceTier: state.spatialVision.selectedServiceTier,
    });
    await state.refreshSessions({ preferLatest: true });
    return result;
  });

state.refreshSessions = async ({ preferLatest = false } = {}) => {
  try {
    const sessionsResult = await listSpatialVisionSessions({ limit: 20 });
    const sessions = (sessionsResult.rows || sessionsResult || []).map(
      normalizeSession,
    );
    const currentSelection = state.spatialVision.selectedSessionId;
    const selectedSessionId =
      preferLatest ||
      !sessions.some(session => String(session.id) === String(currentSelection))
        ? sessions[0]?.id || null
        : currentSelection;
    await patch({
      sessions,
      selectedSessionId,
    });
    scheduleSessionPolling(sessions);
  } catch (error) {
    await patch({ error: error.message || error.response || error });
    throw error;
  }
};

state.cancelSession = async sessionId => {
  const optimisticSessions = markSessionsCanceled(
    session => String(session.id) === String(sessionId),
  );
  await patch({ sessions: optimisticSessions });
  scheduleSessionPolling(optimisticSessions);

  try {
    const result = await cancelSpatialVisionSession(sessionId);
    const sessions = (result.sessions || []).map(normalizeSession);
    const mergedSessions = sessions.length
      ? mergeSessions(sessions)
      : state.spatialVision.sessions;
    await patch({ sessions: mergedSessions });
    scheduleSessionPolling(mergedSessions);
    return result;
  } catch (error) {
    await state.refreshSessions();
    throw error;
  }
};

state.cancelBatch = async batchId => {
  const optimisticSessions = markSessionsCanceled(
    session => String(session.batchId) === String(batchId),
  );
  await patch({ sessions: optimisticSessions });
  scheduleSessionPolling(optimisticSessions);

  try {
    const result = await cancelSpatialVisionBatch(batchId);
    const sessions = (result.sessions || []).map(normalizeSession);
    const mergedSessions = sessions.length
      ? mergeSessions(sessions)
      : state.spatialVision.sessions;
    await patch({ sessions: mergedSessions });
    scheduleSessionPolling(mergedSessions);
    return result;
  } catch (error) {
    await state.refreshSessions();
    throw error;
  }
};

state.toggleDetection = detectionId => {
  const selected = new Set(state.spatialVision.selectedDetectionIds);
  if (selected.has(detectionId)) selected.delete(detectionId);
  else selected.add(detectionId);
  return patch({ selectedDetectionIds: [...selected] });
};

state.toggleSessionDetections = sessionId => {
  const session = state.spatialVision.sessions.find(
    item => item.id === sessionId,
  );
  if (!session) return Promise.resolve();
  const selected = new Set(state.spatialVision.selectedDetectionIds);
  const ids = session.detections.map(detection => detection.id);
  const allSelected = ids.every(id => selected.has(id));
  ids.forEach(id => (allSelected ? selected.delete(id) : selected.add(id)));
  return patch({ selectedDetectionIds: [...selected] });
};

state.addPromptAsCondition = text =>
  withLoading(async () => {
    const cleanText = text.trim();
    if (!cleanText) return null;

    let sequence = getSelectedSequence();
    if (!sequence) {
      sequence = normalizeSequence(
        await createSpatialVisionSequence({
          name: 'Vision Sequence',
          description: 'Created from chat prompts',
          orderIndex: state.spatialVision.sequences.length,
        }),
      );
      await patch({
        // newest first, as the list comes from the backend after a reload
        sequences: [sequence, ...state.spatialVision.sequences],
        selectedSequenceId: sequence.id,
      });
    }

    let step =
      sequence.steps?.find(
        item => String(item.id) === String(state.spatialVision.selectedStepId),
      ) || sequence.steps?.[0];

    if (!step) {
      await createSpatialVisionStep(sequence.id, {
        name: 'Chat checks',
        description: 'Conditions promoted from exploratory chat',
        active: true,
        orderIndex: 0,
      });
      sequence = await refreshSequence(sequence.id);
      step = sequence.steps?.[0];
      await patch({
        selectedSequenceId: sequence.id,
        selectedStepId: step?.id ?? null,
      });
    }

    if (!step) throw new Error('Unable to create a step for this condition');

    await createSpatialVisionCondition(step.id, {
      name: cleanText.slice(0, 255),
      type: 'visual',
      mode: 'text-conversational',
      statusLabel: null,
      value: cleanText,
      orderIndex: step.conditions?.length || 0,
    });

    const refreshed = await refreshSequence(sequence.id);
    await patch({
      selectedSequenceId: refreshed.id,
      selectedStepId: step.id,
    });
    spatialVisionDataChanged.dispatch();
    return refreshed;
  });

state.saveSettings = settings =>
  withLoading(async () => {
    const saved = await updateSpatialVisionSettings(settings);
    await patch({
      settings: { ...state.spatialVision.settings, ...saved, password: '' },
    });
  });

export const SpatialVisionState = state;
export default SpatialVisionState;
